import {
  ArrowDownToLine,
  BadgeCheck,
  BrainCircuit,
  Check,
  Clipboard,
  ExternalLink,
  FileJson,
  MapPinned,
  MessageCircle,
  PackageSearch,
  ShieldAlert,
  Sparkles,
  TimerReset,
  Upload,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { getSupportStatus } from "../lib/support";
import type { DecisionPlan, LocationNode, SupportServerStatus, WarehouseDataset } from "../types";

interface AssistantPageProps {
  dataset: WarehouseDataset;
  plan: DecisionPlan;
  locations: LocationNode[];
  onImport: () => void;
  onOpenSupport: () => void;
}

function downloadFile(content: string, fileName: string, type: string) {
  const url = URL.createObjectURL(new Blob([content], { type: `${type};charset=utf-8` }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

function buildQianfanPrompt(dataset: WarehouseDataset, plan: DecisionPlan, locations: LocationNode[]) {
  const fields = [...new Set(dataset.sales.flatMap((row) => Object.keys(row)))].join("、");
  return `角色：你是仓储经营决策分析智能体，不是数据录入助手。
任务：根据用户上传的仓库出库数据，输出可执行、可审计的库位布局、批次履约和补货方案。

当前数据快照：
- 仓库：${dataset.meta.warehouse}
- 数据日期：${dataset.meta.asOf}
- 记录数：${plan.quality.rows}
- 覆盖天数：${plan.quality.days}
- SKU 数：${plan.products.length}
- 库位数：${locations.length}
- 字段：${fields}
- 数据质量：${plan.quality.title}（完整度 ${(plan.quality.completeness * 100).toFixed(1)}%）

必须遵守：
1. 单品独立计算回归斜率、R² 与波动率，不能只用总销量判断爆热或滞销。
2. 库位编码按“区域-库区-过道-货架”解释，爆旺品优先近端，平滞品建议远端；距离证据不足时不得编造米数。
3. 批次分配先排除召回、过期和待核对批次，再按客户接受的 ¼ / ⅓ / ½ 临期等级筛选，同等级内按 FEFO 出库。
4. 补货周期按热度与证据动态选择 1 / 7 / 30 日；补货量取周期预测区间上界扣除现有库存与在途库存，并说明库存天数与置信度。
5. 数据少于 21 天、字段缺失、R² 较低或没有库位时，必须明确披露不确定性。
6. 不得把演示数据说成真实经营数据，不得声称已执行未发生的现场任务。

输出结构：
A. 决策摘要（3 条以内）
B. 数据质量与证据缺口
C. 商品热度分层（爆旺/平销/平滞）
D. 库位搬迁清单（原库位、建议库位、理由、优先级）
E. 客户约束下的批次分配
F. 补货清单（SKU、周期、预测区间、建议数量、下单日）
G. 风险与人工复核事项`;
}

export function AssistantPage({ dataset, plan, locations, onImport, onOpenSupport }: AssistantPageProps) {
  const [copied, setCopied] = useState<"prompt" | "config" | "">("");
  const [activeTab, setActiveTab] = useState<"brief" | "qianfan">("brief");
  const [supportStatus, setSupportStatus] = useState<SupportServerStatus | null>(null);
  const qianfanPrompt = useMemo(() => buildQianfanPrompt(dataset, plan, locations), [dataset, plan, locations]);
  const qianfanConfig = useMemo(
    () =>
      JSON.stringify(
        {
          agentName: "仓序仓储经营决策体",
          version: "2.0",
          runtime: "baidu-qianfan-compatible",
          input: {
            format: ["csv", "json"],
            requiredFields: ["date", "sku", "product", "quantity"],
            optionalFields: ["location", "lot", "expiresOn", "producedOn", "shelfLifeDays", "customer", "currentStock", "incomingStock", "recallFlag"],
          },
          decisionRules: {
            velocity: "近 7 日与近 3 日出库均量排序；阻尼 Holt + 稳健异常检测识别趋势、周期和突发脉冲",
            placement: "爆旺近端、平销标准带、平滞远端；距离仅在有坐标/库位证据时输出",
            batch: "召回/过期/待核对排除 → ¼ / ⅓ / ½ 客户接受等级 → 同等级内 FEFO",
            replenishmentCycleDays: [1, 7, 30],
          },
          outputSchema: ["quality", "products", "placements", "replenishments", "risks", "summary"],
          safety: ["不虚构数据", "不声称已执行现场任务", "低证据结论必须标注置信度"],
        },
        null,
        2,
      ),
    [],
  );

  useEffect(() => {
    const controller = new AbortController();
    getSupportStatus(controller.signal).then((status) => {
      if (!controller.signal.aborted) setSupportStatus(status);
    });
    return () => controller.abort();
  }, []);

  const fullPlan = useMemo(
    () => ({
      generatedAt: plan.generatedAt,
      dataset: {
        name: dataset.meta.name,
        source: dataset.meta.source,
        asOf: dataset.meta.asOf,
        warehouse: dataset.meta.warehouse,
      },
      quality: plan.quality,
      summary: plan.summary,
      risks: plan.risks,
      products: plan.products.map((item) => ({
        sku: item.sku,
        product: item.product,
        category: item.category,
        slope: item.slope,
        slopePercent: item.slopePercent,
        r2: item.r2,
        forecastDaily: item.forecastDaily,
        confidence: item.confidence,
      })),
      placements: plan.placements,
      replenishments: plan.replenishments,
    }),
    [dataset.meta, plan],
  );

  async function copyText(kind: "prompt" | "config") {
    const text = kind === "prompt" ? qianfanPrompt : qianfanConfig;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      window.setTimeout(() => setCopied(""), 2400);
    } catch {
      setCopied("");
    }
  }

  const evidenceCount = plan.products.filter((item) => item.confidence === "高").length;
  const plannedMoves = plan.placements.filter((item) => item.category === "爆旺").length;
  const urgentOrders = plan.replenishments.filter((item) => item.stockDays <= item.cycle).length;

  return (
    <div className="assistant-page">
      <section className="agent-hero">
        <div className="agent-orb" aria-hidden="true">
          <span />
          <span />
          <span />
          <Sparkles size={27} />
        </div>
        <div className="agent-hero-copy">
          <span className="section-kicker">EXPLAINABLE DECISION AGENT</span>
          <h2>不是把数据放进页面，<br />而是把数据变成下一步动作。</h2>
          <p>
            当前方案由本地可复核规则引擎生成：先检查证据，再判断趋势，
            随后计算空间配位、批次约束和补货节奏。每一条结论都能回到字段与算法。
          </p>
          <div className="agent-actions">
            <button className="primary-button" onClick={onImport}><Upload size={17} /> 导入新数据并分析</button>
            <button
              className="secondary-button"
              onClick={() => downloadFile(JSON.stringify(fullPlan, null, 2), `仓序-完整决策方案-${dataset.meta.asOf}.json`, "application/json")}
            >
              <ArrowDownToLine size={17} /> 导出完整方案
            </button>
          </div>
        </div>
        <div className="agent-status">
          <span><i /> LOCAL ENGINE</span>
          <strong>方案已就绪</strong>
          <small>生成于 {new Date(plan.generatedAt).toLocaleString("zh-CN", { hour12: false })}</small>
          <div><BadgeCheck size={16} /> 证据可追溯</div>
        </div>
      </section>

      <div className="agent-tabs" role="tablist" aria-label="智能体内容切换">
        <button role="tab" aria-selected={activeTab === "brief"} className={activeTab === "brief" ? "is-active" : ""} onClick={() => setActiveTab("brief")}>
          本次决策方案
        </button>
        <button role="tab" aria-selected={activeTab === "qianfan"} className={activeTab === "qianfan" ? "is-active" : ""} onClick={() => setActiveTab("qianfan")}>
          客服 Agent / 证据治理
        </button>
      </div>

      {activeTab === "brief" ? (
        <>
          <section className="agent-brief-header">
            <div>
              <span className="section-kicker">EXECUTIVE BRIEF</span>
              <h3>{plan.headline}</h3>
            </div>
            <div className={`quality-seal seal-${plan.quality.level}`}>
              <span>数据置信</span>
              <strong>{(plan.quality.completeness * 100).toFixed(0)}%</strong>
              <small>{plan.quality.level === "good" ? "可滚动执行" : plan.quality.level === "limited" ? "建议保守执行" : "仅作有限参考"}</small>
            </div>
          </section>

          <section className="agent-decision-grid">
            <div className="decision-narrative">
              <span className="section-kicker">DECISION CHAIN</span>
              {plan.summary.map((line, index) => (
                <div className="decision-line" key={line}>
                  <span>0{index + 1}</span>
                  <p>{line}</p>
                </div>
              ))}
              <div className="decision-line evidence-line">
                <span>04</span>
                <p>{evidenceCount} 个品项具备高置信拟合证据；其余结论已按数据跨度、R² 与缺失字段降级。</p>
              </div>
            </div>
            <div className="agent-action-metrics">
              <div><MapPinned size={19} /><span>近端配位建议</span><strong>{plannedMoves}</strong><small>爆旺品项优先迁入</small></div>
              <div><TimerReset size={19} /><span>当前补货窗口</span><strong>{urgentOrders}</strong><small>需优先释放采购动作</small></div>
              <div><PackageSearch size={19} /><span>独立补货建议</span><strong>{plan.replenishments.length}</strong><small>按 SKU 分别计算</small></div>
            </div>
          </section>

          <section className="agent-plan-sections">
            <article className="plan-section placement-plan">
              <div className="plan-section-head">
                <div><span className="section-kicker">01 / SPACE</span><h3>库位配位方案</h3></div>
                <MapPinned size={21} />
              </div>
              <p className="plan-intro">优先处理有明确距离改善的高频品项；无库位坐标时，只输出相对近远端建议。</p>
              <div className="plan-list">
                {plan.placements.slice(0, 6).map((item, index) => (
                  <div key={item.sku} className="plan-list-row">
                    <span className="plan-rank">{String(index + 1).padStart(2, "0")}</span>
                    <span><strong>{item.product}</strong><small>{item.from} → {item.to}</small></span>
                    <span className="plan-tag">{item.category}</span>
                    <p>{item.reason}</p>
                  </div>
                ))}
                {!plan.placements.length && <div className="empty-state">当前库位与需求带已经匹配，暂无搬迁动作。</div>}
              </div>
            </article>

            <article className="plan-section order-plan">
              <div className="plan-section-head">
                <div><span className="section-kicker">02 / REPLENISH</span><h3>补货下单方案</h3></div>
                <TimerReset size={21} />
              </div>
              <p className="plan-intro">按风险优先级筛选，避免把全部库存预测堆成一张无动作的报表。</p>
              <div className="priority-orders">
                {[...plan.replenishments]
                  .sort((a, b) => a.stockDays / a.cycle - b.stockDays / b.cycle)
                  .slice(0, 7)
                  .map((item) => (
                    <div key={item.sku}>
                      <span><strong>{item.product}</strong><small>{item.sku}</small></span>
                      <span>{item.cycle} 日周期</span>
                      <strong>{item.suggestedQuantity} 件</strong>
                      <small>{item.nextOrderDate}</small>
                    </div>
                  ))}
              </div>
            </article>
          </section>

          <section className="agent-evidence-panel">
            <div className="evidence-panel-head">
              <div><ShieldAlert size={20} /><span><strong>证据与人工复核</strong><small>低置信结论不会被包装成确定事实</small></span></div>
              <span>{plan.risks.length ? `${plan.risks.length} 项提示` : "未发现重大阻断"}</span>
            </div>
            <div className="evidence-panel-body">
              <div>
                <span>数据检查</span>
                {plan.quality.notes.map((note) => <p key={note}>{note}</p>)}
              </div>
              <div>
                <span>执行风险</span>
                {plan.risks.length ? plan.risks.map((risk) => <p key={risk}>{risk}</p>) : <p>当前未识别到期、召回或数据结构阻断项。</p>}
              </div>
            </div>
          </section>
        </>
      ) : (
        <section className="qianfan-panel">
          <div className="qianfan-intro">
            <div className="qianfan-mark"><BrainCircuit size={28} /></div>
            <div>
              <span className="section-kicker">EVIDENCE-FIRST SUPPORT AGENT</span>
              <h3>千帆负责表达，当前数据负责事实。</h3>
              <p>
                仓储客服先由本地规则引擎核对商品、库位、批次和补货事实，再请求服务端千帆 Agent
                组织更自然的回答。千帆未配置、会话失效或网络异常时，会自动保留可核验的本地答案。
              </p>
            </div>
            <div className="support-config-actions">
              <button className="primary-button compact" type="button" onClick={onOpenSupport}>
                <MessageCircle size={15} /> 打开仓储客服
              </button>
              <a href="https://appbuilder.baidu.com/s/TrI8gf4u" target="_blank" rel="noreferrer">
                百度千帆入口 <ExternalLink size={15} />
              </a>
            </div>
          </div>
          <div className={`qianfan-runtime qianfan-runtime-${supportStatus?.qianfan.configured ? "ready" : "local"}`}>
            <span><i aria-hidden="true" /> {supportStatus ? supportStatus.qianfan.configured ? "千帆服务端已配置" : "本地证据模式" : "正在检查服务端"}</span>
            <p>{supportStatus?.qianfan.detail ?? "正在读取 /api/support/chat 的运行时状态；浏览器不会接触服务端密钥。"}</p>
          </div>
          <div className="prompt-grid">
            <article>
              <div className="prompt-head"><span><BrainCircuit size={17} /> 客服事实治理提示词</span><button onClick={() => copyText("prompt")}>{copied === "prompt" ? <Check size={15} /> : <Clipboard size={15} />}{copied === "prompt" ? "已复制" : "复制"}</button></div>
              <textarea value={qianfanPrompt} readOnly aria-label="仓储客服事实治理提示词" />
            </article>
            <article>
              <div className="prompt-head"><span><FileJson size={17} /> Agent 能力配置 JSON</span><button onClick={() => copyText("config")}>{copied === "config" ? <Check size={15} /> : <Clipboard size={15} />}{copied === "config" ? "已复制" : "复制"}</button></div>
              <textarea value={qianfanConfig} readOnly aria-label="仓储客服 Agent 能力配置" />
            </article>
          </div>
          <div className="qianfan-checks">
            <span><Check size={15} /> 密钥仅存 Cloudflare 服务端</span>
            <span><Check size={15} /> 千帆回答不得改写证据数字</span>
            <span><Check size={15} /> 异常时自动回落本地答案</span>
          </div>
          <p className="qianfan-disclosure">
            复制提示词不会自动修改百度千帆的远程应用。生产环境需在 Cloudflare Pages 配置
            QIANFAN_ENDPOINT、QIANFAN_API_KEY 等环境变量，并完成一次真实在线验证。
          </p>
        </section>
      )}
    </div>
  );
}
